import React from "react";
import { Title } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { __RouterContext } from "react-router";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Table,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  page: {
    marginTop: theme.spacing(1),
    minWidth: 0,
    maxWidth: "100%",
    overflow: "visible",
    [theme.breakpoints.down("xs")]: {
      width: "calc(100vw - 16px)",
      maxWidth: "calc(100vw - 16px)",
    },
  },
  pageContent: {
    minWidth: 0,
    padding: theme.spacing(3),
    "&:last-child": { paddingBottom: theme.spacing(3) },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      "&:last-child": { paddingBottom: theme.spacing(2) },
    },
    "& form:not([data-carbon-form='true'])": { display: "grid", gap: theme.spacing(2) },
    "& label": {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.body2.fontSize,
    },
    "& input:not([type='checkbox']):not([type='radio']):not([type='file']):not(.MuiInputBase-input), & select:not(.MuiInputBase-input), & textarea:not(.MuiInputBase-input)": {
      display: "block",
      boxSizing: "border-box",
      width: "100%",
      minHeight: 40,
      marginTop: theme.spacing(0.75),
      padding: theme.spacing(1, 1.5),
      color: theme.palette.text.primary,
      font: "inherit",
      backgroundColor: theme.palette.action.hover,
      border: 0,
      borderBottom: `1px solid ${theme.palette.text.secondary}`,
      borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
      outline: 0,
      transition: theme.transitions.create(["background-color", "border-color"]),
      "&:focus": {
        backgroundColor: theme.palette.action.selected,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
      },
      "&:disabled": { color: theme.palette.text.disabled, cursor: "not-allowed" },
    },
    "& input[type='file']": {
      display: "block",
      marginTop: theme.spacing(1),
      font: "inherit",
    },
    "& table": { width: "100%", borderCollapse: "collapse" },
    "& th, & td": {
      padding: theme.spacing(1.25, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& th": {
      textAlign: "left",
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& th.MuiTableCell-alignRight, & td.MuiTableCell-alignRight": { textAlign: "right" },
    "& ul": { margin: 0, padding: 0, listStyle: "none" },
    "& li": {
      padding: theme.spacing(1.5, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& dl": {
      display: "grid",
      gridTemplateColumns: "minmax(160px, 220px) minmax(0, 1fr)",
      margin: 0,
      maxWidth: 960,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    "& dt, & dd": {
      boxSizing: "border-box",
      minWidth: 0,
      margin: 0,
      padding: theme.spacing(1.25, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
      overflowWrap: "anywhere",
    },
    "& dt": {
      paddingRight: theme.spacing(3),
      color: theme.palette.text.secondary,
      fontSize: theme.typography.body2.fontSize,
    },
  },
  listContent: {
    padding: 0,
    "&:last-child": { paddingBottom: 0 },
    "& $pageIntro": {
      marginBottom: 0,
      padding: theme.spacing(2, 3),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& $filterBar": {
      marginBottom: 0,
      padding: theme.spacing(1, 3),
    },
    "& $sectionHeader": {
      display: "none",
    },
    "& $feedback": {
      borderRight: 0,
      borderLeft: 0,
    },
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      "&:last-child": { paddingBottom: 0 },
      "& $pageIntro": { padding: theme.spacing(2) },
      "& $filterBar": { padding: theme.spacing(1, 2) },
    },
  },
  pageIntro: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  description: { maxWidth: 760 },
  section: {
    minWidth: 0,
    paddingTop: 0,
    "& + &": {
      marginTop: theme.spacing(4),
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  sectionDescription: { marginTop: theme.spacing(0.5), maxWidth: 760 },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    maxWidth: 520,
    marginTop: theme.spacing(1),
  },
  formToolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    boxSizing: "border-box",
    width: "100%",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[100],
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  editSurfaceContent: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
    "& $form": {
      maxWidth: "none",
      marginTop: 0,
    },
  },
  editSurfaceDescription: {
    marginBottom: theme.spacing(1),
  },
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0.5, 1.5),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& > *": {
      flex: "0 1 320px",
      minWidth: 220,
      maxWidth: 320,
    },
  },
  field: { minWidth: 0 },
  fieldLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  required: { color: theme.palette.error.main },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  resourceToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minHeight: 48,
    padding: theme.spacing(0, 1),
  },
  feedback: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minHeight: 52,
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
  feedbackBody: { flex: 1, minWidth: 0 },
  error: {
    borderColor: theme.palette.error.light,
    backgroundColor: theme.palette.error.light,
  },
  status: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: theme.palette.text.primary,
  },
  statusMark: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flex: "0 0 auto",
    backgroundColor: theme.palette.grey[500],
  },
  statusSucceeded: { backgroundColor: theme.palette.success.main },
  statusFailed: { backgroundColor: theme.palette.error.main },
  statusRunning: { backgroundColor: theme.palette.info.main },
  statusPending: { backgroundColor: theme.palette.warning.main },
  tableContainer: {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
  },
  table: {
    minWidth: 600,
    "& th": { fontWeight: theme.typography.fontWeightMedium },
    [theme.breakpoints.down("xs")]: { minWidth: 0 },
  },
  selectableRow: {
    cursor: "pointer",
    transition: theme.transitions.create("background-color"),
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
    "&:hover": { backgroundColor: theme.palette.action.selected },
  },
  tableActions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  technicalDetails: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    "& summary": {
      cursor: "pointer",
      display: "inline-block",
      minHeight: 32,
      padding: theme.spacing(0.75, 0),
      color: theme.palette.primary.main,
      fontSize: theme.typography.body2.fontSize,
    },
    "& > div": {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.divider}`,
      overflowWrap: "anywhere",
    },
  },
}));

export const CarbonActions = ({ children, className = "", ...props }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.actions} ${className}`.trim()} {...props}>
      {children}
    </Box>
  );
};

export const CarbonPageAction = ({ children, to, ...props }) => {
  const router = React.useContext(__RouterContext);
  return router ? (
    <Button component={RouterLink} to={to} {...props}>{children}</Button>
  ) : (
    <Button component="a" href={to} {...props}>{children}</Button>
  );
};

export const CarbonAdminPage = ({
  title,
  description,
  actions,
  children,
  className = "",
  variant = "show",
}) => {
  const classes = useStyles();
  return (
    <>
      <Title title={title} />
      {actions ? (
        <Box className={classes.resourceToolbar}>
          <CarbonActions>{actions}</CarbonActions>
        </Box>
      ) : null}
      <Card className={`${classes.page} ${className}`.trim()}>
        <CardContent className={`${classes.pageContent} ${variant === "list" ? classes.listContent : ""}`.trim()}>
          {description && (
            <Box className={classes.pageIntro}>
              <Typography
                className={classes.description}
                color="textSecondary"
                variant="body2"
              >
                {description}
              </Typography>
            </Box>
          )}
          {children}
        </CardContent>
      </Card>
    </>
  );
};

export const CarbonSection = ({ title, description, actions, children }) => {
  const classes = useStyles();
  return (
    <section className={classes.section}>
      {(title || description || actions) && (
        <Box className={classes.sectionHeader}>
          <Box>
            {title ? <Typography component="h2" variant="h6">{title}</Typography> : null}
            {description ? (
              <Typography
                className={classes.sectionDescription}
                color="textSecondary"
                variant="body2"
              >
                {description}
              </Typography>
            ) : null}
          </Box>
          {actions ? <CarbonActions>{actions}</CarbonActions> : null}
        </Box>
      )}
      {children}
    </section>
  );
};

export const CarbonFormGrid = ({ children, className = "", ...props }) => {
  const classes = useStyles();
  return <Box className={`${classes.formGrid} ${className}`.trim()} {...props}>{children}</Box>;
};

export const CarbonForm = ({ children, className = "", ...props }) => {
  const classes = useStyles();
  return (
    <form
      className={`${classes.form} ${className}`.trim()}
      data-carbon-form="true"
      {...props}
    >
      {children}
    </form>
  );
};

export const CarbonFormToolbar = ({ children, className = "", ...props }) => {
  const classes = useStyles();
  return (
    <Box
      aria-label="Form actions"
      className={`${classes.formToolbar} ${className}`.trim()}
      role="toolbar"
      {...props}
    >
      {children}
    </Box>
  );
};

export const CarbonEditSurface = ({
  children,
  dedicated = false,
  description,
  onClose,
  open,
  title,
}) => {
  const classes = useStyles();
  const titleId = React.useRef(
    `carbon-edit-surface-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
  ).current;

  if (dedicated) {
    return (
      <CarbonSection title={title} description={description}>
        {children}
      </CarbonSection>
    );
  }

  return (
    <Dialog
      aria-labelledby={titleId}
      BackdropProps={process.env.NODE_ENV === "test" ? { transitionDuration: 0 } : undefined}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      transitionDuration={process.env.NODE_ENV === "test" ? 0 : undefined}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent className={classes.editSurfaceContent} dividers>
        {description ? (
          <Typography
            className={classes.editSurfaceDescription}
            color="textSecondary"
            variant="body2"
          >
            {description}
          </Typography>
        ) : null}
        {children}
      </DialogContent>
    </Dialog>
  );
};

export const CarbonFilterBar = ({ children, label = "Filters", className = "", ...props }) => {
  const classes = useStyles();
  return (
    <Box
      aria-label={label}
      className={`${classes.filterBar} ${className}`.trim()}
      role="region"
      {...props}
    >
      {children}
    </Box>
  );
};

export const CarbonConfirmDialog = ({
  cancelLabel = "Cancel",
  confirmLabel = "Confirm",
  description,
  disabled = false,
  onCancel,
  onConfirm,
  open,
  title,
}) => {
  const titleId = React.useRef(
    `carbon-confirm-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
  ).current;
  return (
    <Dialog
      aria-labelledby={titleId}
      onClose={disabled ? undefined : onCancel}
      open={open}
      transitionDuration={process.env.NODE_ENV === "test" ? 0 : undefined}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      {description ? <DialogContent dividers><Typography>{description}</Typography></DialogContent> : null}
      <DialogActions>
        <Button disabled={disabled} onClick={onCancel}>{cancelLabel}</Button>
        <Button color="primary" disabled={disabled} onClick={onConfirm} variant="contained">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CarbonTextInput = ({
  id,
  label,
  name,
  helperText = " ",
  fullWidth = true,
  inputProps = {},
  ...props
}) => {
  const fieldId = id || `carbon-${name || String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <TextField
      fullWidth={fullWidth}
      helperText={helperText}
      id={fieldId}
      inputProps={{ "aria-label": label, ...inputProps }}
      label={label}
      margin="normal"
      name={name}
      variant="filled"
      {...props}
    />
  );
};

export const CarbonSelectInput = ({
  choices = [],
  choiceText = "name",
  choiceValue = "id",
  emptyText = "Select an option",
  helperText = " ",
  id,
  label,
  name,
  SelectProps: selectProps = {},
  ...props
}) => {
  const fieldId = id || `carbon-${name || String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const selectedChoice = choices.find(
    (choice) => String(choice[choiceValue]) === String(props.value ?? ""),
  );
  const selectedLabel = selectedChoice
    ? String(
        typeof choiceText === "function"
          ? choiceText(selectedChoice)
          : selectedChoice[choiceText],
      )
    : undefined;
  return (
    <TextField
      fullWidth
      helperText={helperText}
      id={fieldId}
      InputLabelProps={{ shrink: true }}
      label={label}
      margin="normal"
      name={name}
      select
      SelectProps={{
        native: true,
        ...selectProps,
        inputProps: {
          "aria-label": label,
          title: selectedLabel,
          ...(selectProps.inputProps || {}),
        },
      }}
      variant="filled"
      {...props}
    >
      {emptyText !== null ? <option value="">{emptyText}</option> : null}
      {choices.map((choice) => (
        <option key={choice[choiceValue]} title={typeof choiceText === "function" ? choiceText(choice) : choice[choiceText]} value={choice[choiceValue]}>
          {typeof choiceText === "function" ? choiceText(choice) : choice[choiceText]}
        </option>
      ))}
    </TextField>
  );
};

export const CarbonFileInput = ({
  accept,
  disabled = false,
  fileName,
  id,
  label,
  multiple = false,
  name,
  onChange,
}) => {
  const fieldId = id || `carbon-${name || String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6, margin: "16px 0 8px" }}>
      <span className="MuiTypography-root MuiTypography-body2 MuiTypography-colorTextSecondary">{label}</span>
      <label
        aria-label={`Choose ${label}`}
        aria-disabled={disabled}
        className={`MuiButtonBase-root MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary${disabled ? " Mui-disabled" : ""}`}
        htmlFor={fieldId}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        <span className="MuiButton-label">
          Choose {label}
          <input
            accept={accept}
            aria-label={label}
            disabled={disabled}
            id={fieldId}
            multiple={multiple}
            name={name}
            onChange={onChange}
            style={{ display: "none" }}
            type="file"
          />
        </span>
      </label>
      <span className="MuiTypography-root MuiTypography-caption MuiTypography-colorTextSecondary">
        {fileName || "No file selected"}
      </span>
    </div>
  );
};

export const CarbonField = ({
  label,
  required = false,
  error = false,
  helperText,
  children,
}) => {
  const classes = useStyles();
  const control = React.Children.only(children);
  const id = control.props.id;
  return (
    <Box className={classes.field}>
      <Typography
        className={classes.fieldLabel}
        component="label"
        htmlFor={id}
        variant="body2"
      >
        {label}{required ? <span className={classes.required}> *</span> : null}
      </Typography>
      {control}
      {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
    </Box>
  );
};

export const CarbonFeedback = ({
  loading = false,
  loadingMessage = "Loading…",
  error,
  empty = false,
  emptyMessage = "No records found.",
  onRetry,
  retryLabel = "Try again",
}) => {
  const classes = useStyles();
  if (!loading && !error && !empty) return null;
  if (loading) {
    return (
      <Box className={classes.feedback} role="status" aria-live="polite">
        <CircularProgress size={20} />
        <Typography variant="body2">{loadingMessage}</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box className={`${classes.feedback} ${classes.error}`} role="alert">
        <Typography className={classes.feedbackBody} variant="body2">{error}</Typography>
        {onRetry ? <Button color="primary" onClick={onRetry}>{retryLabel}</Button> : null}
      </Box>
    );
  }
  return (
    <Box className={classes.feedback} role="status">
      <Typography color="textSecondary" variant="body2">{emptyMessage}</Typography>
    </Box>
  );
};

const statusClass = (classes, value) => {
  if (["succeeded", "active", "approved", "published", "complete"].includes(value)) return classes.statusSucceeded;
  if (["failed", "inactive", "rejected", "error"].includes(value)) return classes.statusFailed;
  if (["running", "claimed", "processing"].includes(value)) return classes.statusRunning;
  if (["pending", "draft", "queued", "deprecated"].includes(value)) return classes.statusPending;
  return "";
};

export const CarbonStatus = ({ value = "", label }) => {
  const classes = useStyles();
  const visibleLabel = label ?? String(value).replace(/_/g, " ").replace(/^./, (character) => character.toUpperCase());
  return (
    <Typography className={classes.status} component="span" variant="body2">
      <span
        aria-hidden="true"
        className={`${classes.statusMark} ${statusClass(classes, String(value).toLowerCase())}`.trim()}
      />
      {visibleLabel}
    </Typography>
  );
};

export const CarbonDataTable = ({ label, children, className = "" }) => {
  const classes = useStyles();
  return (
    <TableContainer className={`${classes.tableContainer} ${className}`.trim()}>
      <Table aria-label={label} className={classes.table} size="small">
        {children}
      </Table>
    </TableContainer>
  );
};

export const CarbonTableRow = ({
  ariaLabel,
  children,
  className = "",
  onSelect,
  selected = false,
  ...props
}) => {
  const classes = useStyles();
  const activate = (event) => {
    if (!onSelect) return;
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return;
    if (event.type === "keydown") event.preventDefault();
    onSelect();
  };
  return (
    <TableRow
      aria-label={ariaLabel}
      aria-selected={selected}
      className={`${onSelect ? classes.selectableRow : ""} ${selected ? classes.selectedRow : ""} ${className}`.trim()}
      hover={Boolean(onSelect)}
      onClick={onSelect ? activate : undefined}
      onKeyDown={onSelect ? activate : undefined}
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      {...props}
    >
      {children}
    </TableRow>
  );
};

export const CarbonTableActions = ({ children, label = "Row actions", ...props }) => {
  const classes = useStyles();
  return (
    <Box
      aria-label={label}
      className={classes.tableActions}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      role="toolbar"
      {...props}
    >
      {children}
    </Box>
  );
};

export const CarbonTechnicalDetails = ({ children, label = "Technical details" }) => {
  const classes = useStyles();
  return (
    <details className={classes.technicalDetails}>
      <summary>{label}</summary>
      <div>{children}</div>
    </details>
  );
};
